import { Image, Typography } from 'lib/components';
import { RateType } from 'lib/util';
import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import classes from './index.module.scss';

/**
 * TextField variant options
 * - inline: Label and input on the same line
 * - labelled: Label above the input
 */
type Variant = 'inline' | 'labelled';

/**
 * Props interface for the TextField component
 * Extends HTML input attributes with custom styling and behavior options
 */
interface Props extends React.InputHTMLAttributes<HTMLInputElement & HTMLTextAreaElement> {
	/** Label text for the input field */
	label: string;
	/** Visual style variant */
	variant?: Variant;
	/** Color theme for highlighting (e.g., for validation states) */
	color?: 'primary' | 'danger';
	/** Icon to display at the start of the input */
	startIcon?: React.ReactNode;
	/** Helper text displayed below the input */
	helperText?: string;
	/** Number of rows for textarea variant */
	rows?: number;
	/** Datalist options for autocomplete */
	datalist?: {
		id: string;
		options: string[];
	};
	/** Whether to render as a textarea instead of input */
	textArea?: boolean;
	/** Custom styles for the container */
	containerStyle?: CSSProperties;
	/** Array of symbols to toggle between for number inputs */
	numberSymbol?: string[];
	/** Callback when the increment button is clicked */
	onNumberIncrement?: () => any;
	/** Callback when the decrement button is clicked */
	onNumberDecrement?: () => any;
	/** Initial symbol index for number inputs */
	defaultSymbolIndex?: RateType;
	/** Callback when the symbol is changed */
	onSymbolChange?: (index: RateType) => void;
	/** Whether to hide the symbol toggle */
	noSymbol?: boolean;
}

/**
 * TextField component
 * Renders a customizable text input or textarea with various styling options
 * Supports different variants, colors, and special features like number inputs with symbols
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const TextField = ({
	containerStyle,
	label,
	variant = 'inline',
	startIcon,
	color,
	className,
	datalist,
	textArea,
	rows,
	numberSymbol = ['$', '%'],
	helperText = '',
	defaultSymbolIndex = 0,
	onSymbolChange,
	noSymbol = false,
	...props
}: Props) => {
	// Current index in the numberSymbol array
	const [currentNumberSymbolIndex, setCurrentNumberSymbolIndex] = useState(defaultSymbolIndex);

	/**
	 * Toggle between different number symbols (e.g., $ and %)
	 * Cycles through the numberSymbol array in the specified direction
	 *
	 * @param {('up'|'down')} direction - Direction to cycle through symbols
	 */
	const toggleSymbols = useCallback(
		(direction: 'up' | 'down') => {
			if (direction === 'up') {
				// Calculate next index when going up
				let newIndex = 0;
				if (currentNumberSymbolIndex >= numberSymbol.length - 1) {
					newIndex = 0; // Wrap around to the beginning
				} else {
					newIndex = currentNumberSymbolIndex + 1;
				}

				// Notify parent component of the change
				if (onSymbolChange) {
					onSymbolChange(newIndex);
				}
			} else if (direction === 'down') {
				// Calculate next index when going down
				let newIndex = 0;
				if (currentNumberSymbolIndex === 0) {
					newIndex = numberSymbol.length - 1; // Wrap around to the end
				} else {
					newIndex = currentNumberSymbolIndex - 1;
				}

				// Notify parent component of the change
				if (onSymbolChange) {
					onSymbolChange(newIndex);
				}
			}
		},
		[numberSymbol, currentNumberSymbolIndex, onSymbolChange]
	);

	/**
	 * Update the current symbol index when the default changes
	 * Ensures the component stays in sync with parent component state
	 */
	useEffect(() => {
		if (currentNumberSymbolIndex !== defaultSymbolIndex) {
			setCurrentNumberSymbolIndex(defaultSymbolIndex);
		}
	}, [defaultSymbolIndex, currentNumberSymbolIndex]);

	// Render different layouts based on the variant prop
	switch (variant) {
		case 'inline':
			// Inline variant - label and input on the same line
			return (
				<div className={classes['textFieldInline']} style={containerStyle}>
					<div className={classes['textFieldInline-content']}>
						{/* Optional start icon */}
						<span className={classes['textFieldInline-startIcon']}>{startIcon}</span>
						{/* Input label */}
						<p className={classes['textFieldInline-label']}>{label}:</p>
						{/* Input field */}
						<input className={classes['textFieldInline-input']} {...props} />
					</div>
				</div>
			);

		case 'labelled':
			// Labelled variant - label above the input
			// Apply color class if specified (e.g., for validation states)
			const colorClass = color ? classes['textFieldLabelled-color-' + color] : '';
			// Combine base input class with any custom classes
			let inputClasses = classes['textFieldLabelled-input'];
			if (className) {
				inputClasses += ' ' + className;
			}
			return (
				<div className={`${classes['textFieldLabelled']} ${colorClass}`} style={containerStyle}>
					{/* Input label */}
					<label className={classes['textFieldLabelled-label']}>{label}</label>
					<div style={{ position: 'relative' }}>
						{/* Render either input or textarea based on textArea prop */}
						{!textArea ? (
							<input className={inputClasses} {...props} list={datalist?.id} />
						) : (
							<textarea rows={rows} className={inputClasses} {...props} />
						)}
						{/* Number input symbol toggle - only shown for number inputs when not disabled */}
						{props.type === 'number' && !noSymbol && (
							<div className={classes['textFieldLabelled-input-numberToggle']}>
								{/* Up arrow for cycling symbols */}
								{onSymbolChange !== undefined && (
									<span onClick={() => toggleSymbols('up')}>
										<Image src="icDownCaretSm" />
									</span>
								)}
								{/* Current symbol display */}
								<Typography>{numberSymbol[currentNumberSymbolIndex]}</Typography>
								{/* Down arrow for cycling symbols */}
								{onSymbolChange !== undefined && (
									<span onClick={() => toggleSymbols('down')}>
										<Image src="icDownCaretSm" />
									</span>
								)}
							</div>
						)}
					</div>
					{/* Helper text - only shown if provided */}
					{helperText && <p className={classes['textFieldLabelled-input-helperText']}>{helperText}</p>}
					{/* Datalist for autocomplete options - only shown if provided */}
					{datalist && (
						<datalist id={datalist.id}>
							{datalist.options.map((option) => (
								<option key={option} value={option} />
							))}
						</datalist>
					)}
					{/* Dropdown indicator for datalist inputs */}
					{datalist && <Image src="icDownCaret" className={classes['textFieldLabelled-caret']} />}
				</div>
			);

		default:
			// Fallback to inline variant if an invalid variant is provided
			return (
				<div className={classes['textFieldInline']} style={containerStyle}>
					<div className={classes['textFieldInline-content']}>
						{/* Optional start icon */}
						<span className={classes['textFieldInline-startIcon']}>{startIcon}</span>
						{/* Input label */}
						<p className={classes['textFieldInline-label']}>{label}:</p>
						{/* Input field */}
						<input className={classes['textFieldInline-input']} {...props} />
					</div>
				</div>
			);
	}
};

export default TextField;
